import { forwardRef, ButtonHTMLAttributes, ForwardedRef } from 'react'
import clsx from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'primary' | 'secondary' | 'outline' | 'link'
	size?: 'sm' | 'md' | 'lg'
	fullWidth?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ children, className, variant = 'primary', size = 'md', fullWidth = false, disabled, ...props },
		ref: ForwardedRef<HTMLButtonElement>
	) => {
		return (
			<button
				ref={ref}
				type="button"
				className={clsx(
					'rounded-3xl transition-all duration-300 font-bold flex justify-center items-center disabled:cursor-not-allowed disabled:text-secondary-text disabled:backdrop-blur-lg touch-manipulation',
					{
						// Variants
						'bg-accent-surface hover:bg-primary-hover text-primary-text disabled:bg-tint-surface':
							variant === 'primary',
						'bg-secondary-surface/[0.4] backdrop-blur-lg hover:bg-secondary-button-hover shadow-button text-primary-text disabled:bg-tint-surface':
							variant === 'secondary',
						'bg-primary-surface/[0.4] backdrop-blur-lg border-[0.6px] border-solid border-translucent-plus-border hover:bg-tint-surface text-primary-text disabled:bg-tint-surface':
							variant === 'outline',
						'text-accent-surface hover:text-primary-hover !px-0 !h-auto !bg-transparent':
							variant === 'link',

						// Sizes
						'px-5 h-7 text-sm': size === 'sm',
						'px-6 h-10': size === 'md',
						'px-6 h-12': size === 'lg',

						// Width
						'w-full': fullWidth,
					},
					className
				)}
				disabled={disabled}
				{...props}
			>
				{children}
			</button>
		)
	}
)

// Add display name for better debugging
Button.displayName = 'Button'

export { Button }
export type { ButtonProps }
